import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { DashboardData, formatCompactCount } from '../../../services/dashboard';
import {
  bottomCardSx,
  cardBodySx,
  cardTitleSx,
  chartTooltipProps,
} from './dashboardCardStyles';

const RING_SIZE = 200;

type ProgressCircleProps = {
  value: number;
  min: number;
  max: number;
  size: number;
};

const ProgressCircle = ({ value, min, max, size }: ProgressCircleProps) => {
  const bgStroke = size * 0.17;
  const fgStroke = size * 0.085;
  const radius = size / 2 - bgStroke / 2;
  const circumference = 2 * Math.PI * radius;
  const percent = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const offset = circumference - percent * circumference;

  return (
    <svg width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={radius} stroke="#2a2a2a" strokeWidth={bgStroke} fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#E5E5E5"
        strokeWidth={fgStroke}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
      />
    </svg>
  );
};

type UserPerformanceCardProps = {
  userPerformance: DashboardData['userPerformance'];
};

export function UserPerformanceCard({ userPerformance }: UserPerformanceCardProps) {
  const { score, min, max, newUsers, returningUsers } = userPerformance;

  return (
    <Box sx={{ ...bottomCardSx, py: 2.5 }}>
      <Typography
        sx={{
          ...cardTitleSx,
          textAlign: 'center',
          flexShrink: 0,
          fontSize: 21,
          fontWeight: 600,
        }}
      >
        Overall User Performance
      </Typography>

      <Box sx={{ ...cardBodySx, alignItems: 'center', justifyContent: 'center' }}>
        <Tooltip
          title={
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>Engagement score</Typography>
              <Typography sx={{ fontSize: 12, color: '#ccc' }}>
                {score} of {max} · based on repeat bookings
              </Typography>
            </Box>
          }
          {...chartTooltipProps}
        >
          <Box
            sx={{
              position: 'relative',
              width: RING_SIZE,
              height: RING_SIZE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              cursor: 'default',
              my: 0.5,
            }}
          >
            <ProgressCircle value={score} min={min} max={max} size={RING_SIZE} />
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={{
                  bgcolor: '#232323',
                  borderRadius: '50%',
                  width: 44,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 0.75,
                }}
              >
                <svg width="20" height="20" fill="#fff" viewBox="0 0 24 24">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M12 14c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5z" />
                </svg>
              </Box>
              <Typography sx={{ fontSize: '2.35rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                {score}
              </Typography>
            </Box>
          </Box>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px 24px',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
          px: 1,
          pt: 0.5,
        }}
      >
        <LegendItem
          color="#e5e5e5"
          label="New User"
          value={formatCompactCount(newUsers)}
          hint="Registered in last 30 days"
        />
        <LegendItem
          color="#858585"
          label="Returning User"
          value={formatCompactCount(returningUsers)}
          hint="Users with 2+ bookings"
        />
      </Box>
    </Box>
  );
}

const LegendItem = ({
  color,
  label,
  value,
  hint,
}: {
  color: string;
  label: string;
  value: string;
  hint: string;
}) => (
  <Tooltip title={hint} {...chartTooltipProps}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'default' }}>
      <Box sx={{ width: 13, height: 13, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 14, color: '#bbb', fontWeight: 400 }}>
        {label}{' '}
        <Typography component="span" sx={{ fontWeight: 600, color: '#ddd' }}>
          {value}
        </Typography>
      </Typography>
    </Box>
  </Tooltip>
);
